import ReportSubSection from '@components/ReportSubSection';
import { COMPANY_PRICE_CHART } from '@/constants/feedbackSections';
import { PCHolder } from './styled';
import DeferRenderUntilIntersecting from '@components/DeferRenderUntilIntersecting';
import Statement from '@/components/Statement';
import loadable from '@loadable/component';
import If from '@components/If';
import Description from '@components/Statement/components/Description';
import { useCompanyCanonicalUrl } from '@/hooks/useCompany';
import useCompanyStatementsAPIFormatted from '@/hooks/useCompanyStatementsAPIFormatted';

const ChartActionsContainer = loadable(
  () => import('@components/ChartActions'),
  {
    ssr: false,
    resolveComponent: (component) => component.ChartActionsContainer,
  }
);

const PriceChart = loadable(() => import('@/components/PriceChart'), {
  ssr: false,
  resolveComponent: (component) => component.PriceChart,
});

interface Props {
  id?: string;
  uniqueSymbol?: string;
}

export function SharePrice({ id, uniqueSymbol }: Props) {
  const canonicalUrl = useCompanyCanonicalUrl();
  const { data: statements } = useCompanyStatementsAPIFormatted({
    canonicalURL: canonicalUrl,
  });

  return (
    <ReportSubSection
      title="Latest Share Price and Events"
      trackingProps={{ subject: 'chart' }}
      body={
        <DeferRenderUntilIntersecting preRenderHeight="511">
          {() => (
            <If match={typeof id !== 'undefined'}>
              {() => (
                <PCHolder>
                  <PriceChart lookup={canonicalUrl} />
                  <ChartActionsContainer
                    companyName={uniqueSymbol}
                    feedbackLabel={COMPANY_PRICE_CHART}
                  />
                </PCHolder>
              )}
            </If>
          )}
        </DeferRenderUntilIntersecting>
      }
      statements={
        <>
          {statements?.HasPriceStability ? (
            <Statement
              title={statements?.HasPriceStability?.title}
              description={
                <Description statement={statements.HasPriceStability} />
              }
              value={statements.HasPriceStability.value}
            />
          ) : null}
          <Statement
            title={statements?.HasReturnsVolatilityImprovedOverPastYear?.title}
            description={
              <Description
                statement={statements?.HasReturnsVolatilityImprovedOverPastYear}
              />
            }
            value={statements?.HasReturnsVolatilityImprovedOverPastYear?.value}
          />
        </>
      }
    />
  );
}
